import {
	alpha,
	Box,
	ButtonBase,
	Grid,
	Typography,
	useTheme,
} from "@mui/material";
import {
	type Control,
	Controller,
	type FieldPath,
	type FieldValues,
} from "react-hook-form";
import {
	GOAL_CATEGORY_CONFIG,
	type GoalCategory,
	SELECTABLE_GOAL_CATEGORIES,
} from "web_myplann/Goals/Types/GoalTypes";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props<FormType extends FieldValues, TName> = {
	control: Control<FormType>;
	name: TName;
};

export const GoalCategorySelector = <
	FormType extends FieldValues,
	TName extends FieldPath<FormType>,
>({
	control,
	name,
}: Props<FormType, TName>) => {
	const theme = useTheme();
	const { t } = useResource();

	const categoryLabel: Record<GoalCategory, string> = {
		Housing: t(Resources.Goals.Form.Categories.Housing),
		Car: t(Resources.Goals.Form.Categories.Car),
		Retirement: t(Resources.Goals.Form.Categories.Retirement),
		Education: t(Resources.Goals.Form.Categories.Education),
		Vacation: t(Resources.Goals.Form.Categories.Vacation),
		Custom: t(Resources.Goals.Form.Categories.Custom),
	};

	return (
		<Controller
			control={control}
			name={name}
			render={({ field: { value, onChange } }) => (
				<Box px={1.6} py={1.2} bgcolor={theme.palette.background.paper}>
					<Grid container spacing={1}>
						{SELECTABLE_GOAL_CATEGORIES.map((category) => {
							const config = GOAL_CATEGORY_CONFIG[category];
							const Icon = config.icon;
							const selected = value === category;

							return (
								<Grid key={category} size={4}>
									<ButtonBase
										onClick={() => onChange(category)}
										sx={{
											width: "100%",
											flexDirection: "column",
											gap: 0.6,
											py: 1.2,
											borderRadius: "12px",
											border: `1px solid ${
												selected
													? theme.palette.primary.main
													: theme.palette.grey[200]
											}`,
											bgcolor: selected
												? alpha(theme.palette.primary.main, 0.08)
												: theme.palette.background.paper,
											transition: "all 120ms ease-out",
										}}
									>
										<Box
											sx={{
												width: 32,
												height: 32,
												borderRadius: "12px",
												bgcolor: config.bgColor,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<Icon size={18} color={config.color} />
										</Box>
										<Typography
											fontSize={12}
											fontWeight={500}
											color={theme.palette.grey[700]}
										>
											{categoryLabel[category]}
										</Typography>
									</ButtonBase>
								</Grid>
							);
						})}
					</Grid>
				</Box>
			)}
		/>
	);
};
